/**
 * Example auth api connector that lets your user log in using a defined payload
 * and returns a promise
 */

import api from '@/config/api'
import { EDITOR_SCHEMA_VERSION } from '@/constants'
import vuex from '@/store'
import { getSocketId } from '@/services/socketId'
import catchErrors from './catchErrors'
import Axios from 'axios'
import { uploadParts } from '@/utils/multipartUpload'
import nrcs from '@/api/nrcs'
import groups from '@/api/groups'
import dayjs from 'dayjs'
import _ from 'lodash'
import resolveStationId from '@/utils/resolveStationId'
import v3 from './v3'
import { STORIES_PER_PAGE_MAX } from '@scripps/newsdesk-sdk'

/**
 * v3 stories list (ND-2357).
 *
 * Every stories list caller in the app goes through storiesListParams, so the
 * option names the callers still use (startDate, stations, groupId, deleted,
 * ...) are translated into the v3 vocabulary in exactly one place. The SDK
 * (packages/newsdesk-sdk) speaks pure v3 and knows nothing about the store;
 * station names resolve to ids and dates land in the workspace timezone here.
 */
const V3_PAGE_SIZE = 100
const DEFAULT_STORY_INCLUDE = 'assignments,tasks,media_assets,share_requests'

/** Y-m-d in the workspace timezone; a Y-m-d string passes through untouched. */
function toWorkspaceDay(value) {
  if (value == null || value === '') return undefined
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value)
  return dayjs(value).tz(vuex.getters.currentTimezone).format('YYYY-MM-DD')
}

/** Station names, ids or objects (array or comma list) to ids; nothing means the current station. */
function stationIdsFor(stations) {
  const list = Array.isArray(stations) ? stations : typeof stations === 'string' ? stations.split(',') : stations ? [stations] : []
  const given = list.map((s) => (typeof s === 'string' ? s.trim() : s)).filter((s) => s !== '' && s != null)
  if (!given.length) return [resolveStationId()]
  return [...new Set(given.map((s) => resolveStationId(s)))]
}

/**
 * App-level list options to v3 list params ({ filter, sort, include }).
 * `filter` keys pass through by name; a null value is kept because it is the
 * API's IS NULL form (the "no value" group). v2 scope words are not accepted
 * here: callers name the window (dateWindow: 'today' | 'tomorrow' |
 * 'today_and_tomorrow' | 'from_today' | 'from_tomorrow') plus includeUndated.
 */
function storiesListParams({
  filter = {},
  stations,
  groupId,
  id,
  masterId,
  startDate,
  endDate,
  dateWindow,
  includeUndated,
  keywords,
  simpleSearch,
  rootOnly,
  deleted,
  trashed,
  extraCheckArchiveRundowns,
  assignedTo,
  pitchedBy,
  promotedNow,
  include,
  sort
} = {}) {
  const f = {}
  Object.keys(filter ?? {}).forEach((key) => {
    if (filter[key] !== undefined) f[key] = filter[key]
  })
  if (groupId) f.workspace_group_id = groupId
  else f.station_id = stationIdsFor(stations)
  if (id != null) f.id = id
  if (masterId != null) f.master_id = masterId
  const from = toWorkspaceDay(startDate)
  const to = toWorkspaceDay(endDate)
  if (from) f.story_date_from = from
  if (to) f.story_date_to = to
  if (dateWindow) f.date_window = dateWindow
  if (includeUndated) f.include_undated = true
  if (keywords) {
    f.keywords = keywords
    if (simpleSearch) f.simple_search = true
  }
  if (rootOnly) f.root_only = true
  if (trashed) f.trashed = trashed
  else if (deleted) f.trashed = 'only'
  if (extraCheckArchiveRundowns) f.has_archive_rundowns = true
  if (assignedTo) f.assigned_to = assignedTo
  if (pitchedBy) f.pitched_by = pitchedBy
  if (promotedNow) f.promoted_now = true

  let sortValue = sort || undefined
  if (!sortValue && (deleted || trashed === 'only')) sortValue = '-deleted_at'

  return { filter: f, sort: sortValue, include: include || undefined }
}

/** The app's axios cache policy, carried through the SDK's opaque transport options. */
function cachePolicy(maxAge, ignoreCache) {
  return { transport: { cache: { maxAge, ignoreCache: ignoreCache ?? false } } }
}

export default {
  ...groups,
  getServerInfo() {
    return api.get('/v2/newsdesk/server-info')
  },
  validateToken() {
    return api.get('/v2/auth/validate')
  },
  // QA impersonation (ND-2067): global admins, non-production only. The API
  // answers 403 in production, which the caller reads as "not available".
  startImpersonation(empid) {
    return api.post('/v2/auth/impersonate', { empid })
  },
  stopImpersonation() {
    return api.delete('/v2/auth/impersonate')
  },
  getActiveImpersonation() {
    // Polled to notice expiry, so it must never be answered from the cache.
    return api.get('/v2/auth/impersonate/active', { cache: { maxAge: 0, ignoreCache: true } })
  },
  getStationAccessList() {
    return catchErrors.get(`/v2/newsdesk/access`)
  },
  getNewsDeskUsers(station = null, level = null) {
    return catchErrors.get(`/v2/newsdesk/users/by-station`, {
      params: { 'filter[station_id][]': resolveStationId(station), ...(level ? { level } : {}) },
      cache: {
        maxAge: 600 * 1000
      }
    })
  },

  getUserProfile(userId) {
    return catchErrors.get(`/v2/newsdesk/users/${userId}`, {
      params: {
        includeAccess: true
      },
      cache: {
        maxAge: 120 * 1000
      }
    })
  },

  fetchUsers({ filter, sort, limit = 100, page } = {}, skipCache = false) {
    let params = {}

    if (filter) {
      Object.keys(filter).forEach((key) => {
        if (filter[key] != null && filter[key] !== '') {
          params[`filter[${key}]`] = filter[key]
        }
      })
    }

    if (sort) params.sort = sort
    if (page) params.page = page
    if (limit) params.per_page = limit

    return catchErrors.get(`/v2/newsdesk/users`, {
      params,
      cache: {
        maxAge: 30 * 1000,
        ignoreCache: skipCache
      }
    })
  },

  getExtraContacts(config = {}) {
    const stationId = vuex.getters.currentStation.id
    return api.get(`/v2/newsdesk/users/extra`, {
      params: { 'filter[station_id][]': stationId },
      cache: {
        maxAge: 600 * 1000
      },
      ...config
    })
  },

  addExtraContact({ name, email }) {
    const stationId = vuex.getters.currentStation.id
    return api.post(`/v2/newsdesk/users/extra`, { name, email }, { params: { 'filter[station_id][]': stationId } })
  },

  saveExtraContact({ id, name, email, newStation }) {
    const station = vuex.getters.currentStation.station
    const data = { name, email }
    if (newStation && newStation !== station) data.station = newStation
    return api.patch(`/v2/newsdesk/users/extra/${id}`, data)
  },

  deleteExtraContact(id) {
    return api.delete(`/v2/newsdesk/users/extra/${id}`)
  },

  getStations(extraParams = {}) {
    let params = {
      limit: 100,
      ...extraParams
    }
    return catchErrors.get(`/v2/newsdesk/stations`, { params })
  },
  getStationSettings(workspace) {
    const station = workspace || vuex.getters.currentStation.station
    if (!station) return
    return catchErrors.get(`/v2/newsdesk/stations/${station}`)
  },
  getStationConfigurations() {
    return catchErrors.get(`/v2/newsdesk/stations`)
  },
  addStationSettings(data) {
    return catchErrors.post('/v2/newsdesk/stations', data)
  },
  saveStationSettings(data) {
    // Don't include task and service
    const { task, service, ...settings } = data
    return catchErrors.patch(`/v2/newsdesk/stations`, settings)
  },
  getStationSecurity(station) {
    return catchErrors.get(`/v2/newsdesk/security`, { params: { 'filter[station_id][]': resolveStationId(station), assoc: true } })
  },
  saveStationSecurity(data, station) {
    return catchErrors.patch(`/v2/newsdesk/security`, data, { params: { 'filter[station_id][]': resolveStationId(station) } })
  },
  // Graphic Templates (F-09)
  getGraphicTemplates(params) {
    return catchErrors.get('/v2/newsdesk/graphic-templates', { params })
  },
  createGraphicTemplate(data) {
    return catchErrors.post('/v2/newsdesk/graphic-templates', data)
  },
  updateGraphicTemplate(id, data) {
    return catchErrors.patch(`/v2/newsdesk/graphic-templates/${id}`, data)
  },
  deleteGraphicTemplate(id) {
    return catchErrors.delete(`/v2/newsdesk/graphic-templates/${id}`)
  },

  // Connectors (F-30 CONN)
  getConnectors(params) {
    const filterParams = {}
    if (params?.is_active !== undefined) filterParams['filter[is_active]'] = params.is_active
    if (params?.connection_type !== undefined) filterParams['filter[connection_type]'] = params.connection_type
    return catchErrors.get('/v2/newsdesk/connectors', { params: filterParams, cache: { maxAge: 0, ignoreCache: true } })
  },
  getConnector(id) {
    return catchErrors.get(`/v2/newsdesk/connectors/${id}`, { cache: { maxAge: 0, ignoreCache: true } })
  },
  addConnector(data) {
    return catchErrors.post('/v2/newsdesk/connectors', data)
  },
  updateConnector(id, data) {
    return catchErrors.patch(`/v2/newsdesk/connectors/${id}`, data)
  },
  deleteConnector(id) {
    return catchErrors.delete(`/v2/newsdesk/connectors/${id}`)
  },
  discoverConnectorOptions(stationName, connectorId, payload) {
    return catchErrors.post(`/v2/newsdesk/stations/${stationName}/connectors/${connectorId}/discover`, payload)
  },

  getStagehandSettings(station) {
    let _station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/stagehand/settings/${_station}`)
  },
  saveStagehandSettings(data) {
    return catchErrors.patch(`/v2/stagehand/settings/${data.station}`, data)
  },
  getStagehandSystems(station) {
    let _station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/stagehand/${_station}/systems`, {
      cache: {
        maxAge: 1000
      },
      timeout: 0
    })
  },
  getStagehandInfo(station) {
    let _station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/stagehand/${_station}/info`)
  },
  stopStagehandSystem(systemName) {
    let _station = vuex.getters.currentStation.station
    return catchErrors.patch(`/v2/stagehand/${_station}/systems/${systemName}`, { data: {} }, { params: { stop: true } })
  },
  startStagehandSystem(systemName) {
    let _station = vuex.getters.currentStation.station
    return catchErrors.patch(`/v2/stagehand/${_station}/systems/${systemName}`, { data: {} }, { params: { start: true } })
  },
  getWorkspaceStats(station) {
    return catchErrors.get(`/v2/newsdesk/workspace/stats`, {
      params: { 'filter[station_id][]': resolveStationId(station) },
      cache: {
        maxAge: 30 * 1000
      }
    })
  },
  getStagehandJobs(station) {
    let _station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/stagehand/${_station}/jobs?include=watchables`)
  },
  getPipelineDetails(id) {
    return catchErrors.get(`/v2/stagehand/pipelines/${id}`)
  },

  getMyTasks(skipCache) {
    // NOT migrated: the new bare `GET tasks` route (StoryTaskController::index)
    // has no query-param fallback for status/assigned (only station_name does) —
    // switching this to the new route would silently drop the "active, assigned
    // to me" filter and return every task for the station instead. Needs a small
    // backend fix (the same $status ?: $request->query('status', '') fallback
    // pattern already used for station_name everywhere else) before this call
    // can move off the legacy path. Flagged in the ND-1490 changelog.
    let station = vuex.getters.currentStation.station
    return catchErrors.get(`/v2/newsdesk/tasks/${station}/active/assigned?include=story`, {
      cache: {
        maxAge: 10 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getMyAssignments(skipCache, station) {
    station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/newsdesk/assignments`, {
      params: {
        station
      },
      cache: {
        maxAge: 120 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  saveTask(story_id, data) {
    if (data.id) {
      // update task
      return catchErrors.patch(`/v2/newsdesk/tasks/${story_id}/${data.id}`, data)
    } else {
      // add task
      return catchErrors.post(`/v2/newsdesk/tasks/${story_id}`, data)
    }
  },
  deleteTask(story_id, id) {
    if (id) {
      // delete task
      return catchErrors.delete(`/v2/newsdesk/tasks/${story_id}/${id}`)
    }
  },

  // welcome links

  saveWelcomeLink(data) {
    const stationId = vuex.getters.currentStation.id
    if (data.id) {
      // update link
      return catchErrors.patch(`/v2/newsdesk/links/${data.id}`, data)
    } else {
      // add link
      return catchErrors.post(`/v2/newsdesk/links`, data, { params: { 'filter[station_id][]': stationId } })
    }
  },
  getWelcomeLinks() {
    const stationId = vuex.getters.currentStation.id
    return catchErrors.get(`/v2/newsdesk/links`, { params: { 'filter[station_id][]': stationId } })
  },
  deleteWelcomeLink(data) {
    if (data.id) {
      // delete link
      return catchErrors.delete(`/v2/newsdesk/links/${data.id}`)
    }
  },

  // splits

  getSplits() {
    const stationId = vuex.getters.currentStation.id
    return api.get(`/v2/newsdesk/splits`, { params: { 'filter[station_id][]': stationId } })
  },
  saveSplit(data) {
    const stationId = vuex.getters.currentStation.id
    if (data.id) {
      // update
      return api.patch(`/v2/newsdesk/splits/${data.id}`, data)
    } else {
      // add
      return api.post(`/v2/newsdesk/splits`, data, { params: { 'filter[station_id][]': stationId } })
    }
  },
  deleteSplit(data) {
    if (data.id) {
      // delete
      return api.delete(`/v2/newsdesk/splits/${data.id}`)
    }
  },

  clearCacheKey(key) {
    if (!key) throw new Error('No cache key provided')
    return api.delete(`/sudo/admin/show-cache/${key}`)
  },
  flushCache() {
    return api.delete(`/sudo/admin/show-cache/`)
  },
  forceQueuedJobs() {
    return api.get(`/schedule-heartbeat?access_key=89fk1ee01D54734a99789fdAec3afQb34a95`, {
      cache: {
        ignoreCache: true
      }
    })
  },
  fetchOktaGroups({ filter, sort, limit = 50, page } = {}, skipCache = false) {
    let params = {}
    if (filter) {
      Object.keys(filter).forEach((key) => {
        if (filter[key] != null && filter[key] !== '') {
          params[`filter[${key}]`] = filter[key]
        }
      })
    }
    if (sort) params.sort = sort
    if (page) params.page = page
    if (limit) params.per_page = limit
    return catchErrors.get(`/v2/okta/groups/paginated`, {
      params,
      cache: { maxAge: 30 * 1000, ignoreCache: skipCache }
    })
  },
  fetchOktaGroupMembers(groupId, { filter, sort, limit = 50, page } = {}, skipCache = false) {
    let params = {}
    if (filter) {
      Object.keys(filter).forEach((key) => {
        if (filter[key] != null && filter[key] !== '') {
          params[`filter[${key}]`] = filter[key]
        }
      })
    }
    if (sort) params.sort = sort
    if (page) params.page = page
    if (limit) params.per_page = limit
    return catchErrors.get(`/v2/okta/groups/${groupId}/members`, {
      params,
      cache: { maxAge: 30 * 1000, ignoreCache: skipCache }
    })
  },
  getOktaGroups(station) {
    let ou = station ?? ''

    if (station == 'national') {
      ou = 'kmgh'
    } else if (station == 'ews') {
      ou = 'corporate'
    }

    if (ou.length > 1) {
      return api.get(`/v2/okta/groups/ou/${ou}`, {
        cache: {
          maxAge: 300 * 1000
        }
      })
    } else {
      return api.get(`/v2/okta/groups`, {
        cache: {
          maxAge: 600 * 1000
        }
      })
    }
  },
  syncOktaGroups() {
    return catchErrors.post(`/v2/okta/groups/sync`, {}, { cache: { ignoreCache: true } })
  },
  syncOktaGroupMembers(stationId) {
    return catchErrors.post(
      `/v2/okta/stations/${stationId}/sync-group-members`,
      {},
      {
        cache: {
          ignoreCache: true
        }
      }
    )
  },
  getColumnOptions(column, skipCache = false, station) {
    return api.get(`/v2/newsdesk/${column}/options`, {
      params: { 'filter[station_id][]': resolveStationId(station) },
      cache: {
        ignoreCache: skipCache
      }
    })
  },
  getAllColumnOptions(skipCache = false, station) {
    return api.get(`/v2/newsdesk/options`, {
      params: { 'filter[station_id][]': resolveStationId(station) },
      cache: {
        ignoreCache: skipCache
      }
    })
  },
  saveColumnOption(column, data, station) {
    return api.post(`/v2/newsdesk/${column}/options`, data, { params: { 'filter[station_id][]': resolveStationId(station) } })
  },
  deleteColumnOption(column, id, station) {
    return api.delete(`/v2/newsdesk/${column}/options/${id}`)
  },
  syncColumnOptions(column) {
    // let station = vuex.getters.currentStation.station
    // return api.post(`/v2/newsdesk/${column}/options/${station}/sync`)
    return true
  },

  getTags(skipCache = false, type = 'ALL', station) {
    return api.get(`/v2/newsdesk/tags`, {
      params: { 'filter[station_id][]': resolveStationId(station), type },
      cache: {
        ignoreCache: skipCache
      }
    })
  },
  saveTag(type, data, station) {
    return api.post(`/v2/newsdesk/tags/${type}`, data, { params: { 'filter[station_id][]': resolveStationId(station) } })
  },
  deleteTag(type, id, station) {
    return api.delete(`/v2/newsdesk/tags/${type}/${id}`)
  },

  addStoryPitch(data) {
    const stationId = vuex.getters.currentStation.id
    return catchErrors.post(`/v2/newsdesk/stories/pitch`, data, {
      params: { 'filter[station_id][]': stationId },
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },

  addStoryFolo(parentId, dates, mediaAssetIds = []) {
    const payload = { dates: dates }
    // Selected assets move to the (latest-dated) folo so the 30-day expiration
    // keys off the folo's story_date instead of the original's.
    if (mediaAssetIds.length) payload.media_asset_ids = mediaAssetIds
    return catchErrors.post(`/v2/newsdesk/stories/${parentId}/folo`, payload)
  },

  mergeStory(from_story_id, to_story_id) {
    const stationId = vuex.getters.currentStation.id
    let data = {
      from_story_id, // Required
      to_story_id
    }

    // convert to formdatavar form_data = new FormData();
    let formData = new FormData()
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key])
    })

    return catchErrors.post(`/v2/newsdesk/stories/merge`, formData, {
      params: { 'filter[station_id][]': stationId },
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },
  addAttachments(story_id, data) {
    const stationId = vuex.getters.currentStation.id
    return api.post(`/v2/newsdesk/attachments/story/${story_id}`, data, {
      params: { 'filter[station_id][]': stationId },
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      },
      maxBodyLength: Infinity,
      maxContentLength: Infinity
    })
  },

  // NOTE: dead code — the only call site is commented out (store/modules/stories.js).
  // Left migrated to the new convention for consistency; not verified functionally.
  getAttachments(scope, skipCache) {
    const stationId = vuex.getters.currentStation.id
    return api.get(`/v2/newsdesk/attachments/${scope}`, {
      params: { 'filter[station_id][]': stationId },
      cache: {
        maxAge: 45 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },

  getAttachmentsForStory(scope, id, skipCache) {
    const stationId = vuex.getters.currentStation.id
    return api.get(`/v2/newsdesk/attachments/${scope}/${id}`, {
      params: { 'filter[station_id][]': stationId },
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  deleteAttachment(id) {
    return api.delete(`/v2/newsdesk/attachments/${id}`)
  },

  async completeMultiPartUpload(assetId, uploadId, parts, fileName) {
    let data = {
      uploadId,
      parts,
      fileName
    }
    const completionResponse = await catchErrors.patch(`/v2/newsdesk/media_assets/complete_upload/${assetId}`, data, {})
    if (completionResponse?.success === false) throw new Error(completionResponse?.msg ?? 'Failed to complete upload')
    return completionResponse
  },

  async abortMultiPartUpload(assetId, fileName) {
    let data = {
      fileName
    }
    const completionResponse = await catchErrors.patch(`/v2/newsdesk/media_assets/abort_upload/${assetId}`, data, {})
    if (completionResponse?.success === false) throw new Error(completionResponse?.msg ?? 'Failed to complete upload')
    return completionResponse
  },

  uploadMediaAssetMultiPart(file, multiPartUpload, progressCallback, cancelToken) {
    if (!multiPartUpload?.chunkSize) return

    return uploadParts({
      file,
      chunkSize: multiPartUpload.chunkSize,
      preSignedUrls: multiPartUpload.preSignedUrls,
      cancelToken,
      onProgress: progressCallback,
      put: (url, blob, config) => Axios.put(url, blob, { ...config, timeout: 0 })
    })
  },

  getCancelTokenSource() {
    const CancelToken = Axios.CancelToken
    const source = CancelToken.source()
    return source
  },

  async addMediaAsset(jsonData, progressCallback, cancelToken, mediaCreatedCallback) {
    let file = jsonData.file
    if (!(file instanceof File)) throw new Error('Data passed is not a File')
    let data = {
      ...jsonData,
      filename: file.name,
      size: file.size,
      // Browser File API surfaces the MIME type here; forward it so the API
      // persists mime_type on MediaAssetLocation (CreateMediaAssetRepository
      // reads `mime_type` first, then falls back to legacy `type`).
      type: file.type
    }

    delete data.file

    const mediaAssetResponse = await catchErrors.post(`/v2/newsdesk/media_assets`, data)
    let multiPartUpload = mediaAssetResponse?.data?.multiPartUpload ?? []
    let newAsset = mediaAssetResponse?.data?.media_asset
    try {
      mediaCreatedCallback(newAsset)
    } catch (error) {
      console.error('Failed to call mediaCreatedCallback')
    }
    try {
      const parts = await this.uploadMediaAssetMultiPart(file, multiPartUpload, progressCallback, cancelToken)
      const uploadResponse = await this.completeMultiPartUpload(newAsset.id, multiPartUpload.uploadId, parts, file.name)
      return { mediaAsset: mediaAssetResponse?.data?.media_asset, upload: uploadResponse?.data }
    } catch (error) {
      if (newAsset?.id) await this.abortMultiPartUpload(newAsset?.id, file.name)
      throw error
    }
  },

  updateMediaAsset(id, data) {
    return catchErrors.patch(`/v2/newsdesk/media_assets/${id}`, data)
  },
  cloneMediaAsset(id, data) {
    return catchErrors.post(`/v2/newsdesk/media_assets/${id}/clone`, data)
  },
  /**
   * Hybrid story + asset search for the "Add existing clip" picker (ND-1751).
   * Returns { data: [{ story, media_assets, matched_asset_ids, matched_on, score }], meta }.
   *
   * @param {{ station: string, q?: string, scope?: 'current'|'all', excludeAssetIds?: number[], excludeStoryId?: number, page?: number, perPage?: number }} args
   */
  searchMediaAssets({ station, q = '', scope = 'current', excludeAssetIds = [], excludeStoryId = null, page = 1, perPage = 20 }) {
    return catchErrors.get(`/v2/newsdesk/media_assets/search`, {
      params: {
        station_name: station ?? vuex.getters.currentStation.station,
        q: q || undefined,
        scope,
        exclude_asset_ids: excludeAssetIds.length ? excludeAssetIds : undefined,
        exclude_story_id: excludeStoryId || undefined,
        page,
        per_page: perPage
      },
      cache: { ignoreCache: true }
    })
  },
  /**
   * Move or copy media assets to another story in the same workspace.
   * Validation is all-or-nothing on the API side; a 422 names the offending asset ids.
   *
   * @param {{ mediaAssetIds: number[], destinationStoryId: number, mode: 'move'|'copy' }} args
   * @returns Promise<{data: Array<MediaAsset>}> - the moved rows, or the new copies
   */
  transferMediaAssets({ mediaAssetIds, destinationStoryId, mode }) {
    return catchErrors.post(`/v2/newsdesk/media_assets/transfer`, {
      media_asset_ids: mediaAssetIds,
      destination_story_id: destinationStoryId,
      mode
    })
  },

  transcribeMediaAsset(id) {
    return catchErrors.post(`/v2/newsdesk/media_assets/${id}/transcribe`)
  },

  // Media Assets - push existing media asset to video uploader
  pushMediaAsset({ destinations, id, targetFilename }) {
    return catchErrors.post(`/v2/newsdesk/media_assets/push`, { destinations: destinations?.join(','), id, targetFilename })
  },

  getMediaAsset(id, skipCache) {
    return catchErrors.get(`/v2/newsdesk/media_assets/${id}`, {
      cache: {
        maxAge: 45 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },

  /**
   * Replace a video asset's thumbnail with an image (ND-2415, API ND-2413).
   * @param {number} id - Media asset ID
   * @param {Blob} image - JPEG or PNG, at most 3MB
   * @returns the updated media asset
   */
  async uploadMediaAssetThumbnail(id, image) {
    const formData = new FormData()
    formData.append('image', image, image.name || 'thumbnail.jpg')
    const resp = await catchErrors.post(`/v2/newsdesk/media_assets/${id}/thumbnail`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
    return resp?.data
  },

  /**
   * Clear a video asset's thumbnail override, back to the VMS thumbnail (ND-2415).
   * @param {number} id - Media asset ID
   * @returns the updated media asset
   */
  async resetMediaAssetThumbnail(id) {
    const resp = await catchErrors.delete(`/v2/newsdesk/media_assets/${id}/thumbnail`)
    return resp?.data
  },

  getMediaAssetVariantUrl(id, variant) {
    return catchErrors.get(`/v2/newsdesk/media_assets/${id}/${variant}`, {
      maxRedirects: 0,
      validateStatus: (status) => status === 200 || status === 302,
      cache: {
        ignoreCache: true
      }
    })
  },
  /* getMediaAssets - get media assets
   *
   * @param {Object} params - Query parameters
   * @param {Array<string>} params.stations - Station codes to filter by (required, defaults to current station)
   * @param {string} [params.keywords] - Search keywords for partial match on name and other fields
   * @param {string} [params.name] - Filter by asset name
   * @param {string} [params.master_id] - Filter by master media asset ID
   * @param {number} [params.limit=400] - Max results to return
   * @param {number} [params.offset=0] - Pagination offset
   * @param {boolean} [params.hasErrors] - Filter by error status
   * @param {string} [params.include] - Related data to include (e.g. 'story')
   * @param Boolean skipCache
   * @returns Promise<{data: {count, offset, total, results: Array<MediaAsset>}}>
   * Response includes pagination: count (items in this page), offset (current position), total (total available)
   */
  getMediaAssets(params, skipCache = false) {
    if (!params) params = { include: 'story' }
    if (!params.stations) params.stations = [vuex.getters.currentStation.station]
    if (!params.limit) params.limit = 400

    return catchErrors.get(`/v2/newsdesk/media_assets`, {
      params,
      cache: {
        maxAge: 45 * 1000,
        ignoreCache: skipCache
      }
    })
  },

  /* getMediaForStory - get media assets linked to a specific story
   *
   * @param {number|string} id - Story ID
   * @param {boolean} skipCache - Skip cache
   * @returns Promise<{data: Array<MediaAsset>}> - Plain array of media assets for the story
   */
  getMediaForStory(id, skipCache) {
    return catchErrors.get(`/v2/newsdesk/story_media_assets/master/${id}`, {
      cache: {
        maxAge: 45 * 1000,
        ignoreCache: skipCache
      }
    })
  },
  /* getScriptMediaForShare - media a story's scripts air, which sharing the story always copies
   *
   * An asset with is_shareable_across_workspaces false stays on the original instead.
   *
   * @param {number|string} id - Story ID
   * @returns Promise<{data: Array<MediaAsset>}> - Plain array of media assets
   */
  getScriptMediaForShare(id) {
    return catchErrors.get(`/v2/newsdesk/share_requests/script_media/${id}`)
  },
  deleteMediaAsset(id) {
    return catchErrors.delete(`/v2/newsdesk/media_assets/${id}`)
  },
  async downloadMediaAsset(id, variant = 'original') {
    const response = await api
      .post(
        `/v2/newsdesk/media_assets/${id}/${variant}/download`,
        {},
        {
          maxRedirects: 0,
          validateStatus: (status) => status === 200 || status === 302
        }
      )
      .then()
      .catch((err) => {
        console.log('assets download error', err)
      })

    const signedUrl = response?.data?.signedURL || null

    if (!signedUrl) {
      throw new Error('No signed URL found in redirect response')
    }

    return signedUrl
  },

  getComments(scope, id, skipCache = false) {
    return api.get(`/v2/newsdesk/comments/${scope}/${id}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
  },
  sendComment(story_id, comment, mediaAssetId) {
    return api.post(`/v2/newsdesk/comments/${story_id ?? ''}`, {
      text: comment,
      media_asset_id: mediaAssetId
    })
  },
  updateComment(comment) {
    return api.patch(`/v2/newsdesk/comments/${comment.id}`, comment)
  },
  deleteComment(id) {
    return api.delete(`/v2/newsdesk/comments/${id}`)
  },
  setCommentStickyStatus(id, sticky) {
    return api.patch(`/v2/newsdesk/comments/${id}/sticky/${sticky}`)
  },
  getContacts() {
    let station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/contacts/${station}?updated_at>=0`)
  },
  addContact(data, station) {
    return api.post(`/v2/newsdesk/contacts/${station}`, data)
  },
  updateContact(id, data) {
    return api.patch(`/v2/newsdesk/contacts/${id}`, data)
  },
  deleteContact(id) {
    return api.delete(`/v2/newsdesk/contacts/${id}`)
  },
  async shareContact(id, story_id) {
    // catchErrors raises on an `errors` payload returned alongside a 200. The share
    // endpoint also reports failure as `success: false`, which it does not cover, so
    // that case is turned into a throw here rather than in each caller. An older API
    // returns an empty body on success, so only an explicit false is a failure.
    const response = await catchErrors.post(`/v2/newsdesk/contacts/${id}/share/${story_id}`)

    if (response.data?.success === false) {
      throw new Error(response.data.err_msg || 'Sharing failed.')
    }

    return response
  },

  // waiting on API changes for issue #903
  // shareContact(id, story_id) {
  //   return api.post(`/v2/newsdesk/contacts/${id}/share/${story_id}`)
  // },

  getStandardLogs(station, level) {
    const params = { 'filter[station_id][]': resolveStationId(station) }
    if (level) params.level = level
    return api.get(`/v2/newsdesk/logs/standard`, { params })
  },
  getFormats() {
    const stationId = vuex.getters.currentStation.id
    return api.get(`/v2/newsdesk/format/options`, { params: { 'filter[station_id][]': stationId } })
  },

  async getStory({ id, station, includeDetails = false }, skipCache = false, deleted = false) {
    const params = storiesListParams({
      id,
      stations: station ? [station] : undefined,
      // "deleted" callers mean "find it even if it is in the trash"
      trashed: deleted ? 'with' : undefined,
      include: includeDetails ? 'tasks,assignments,comments,attachments,media_assets,share_requests' : undefined
    })
    const { data } = await v3.stories.list({ ...params, per_page: 1 }, cachePolicy(15 * 1000, skipCache))
    if (!data?.length) {
      throw new Error(`Story with ID ${id} (station: ${station ?? vuex.getters.currentStation.station}) not found`)
    }
    return data[0]
  },
  async getStoryById(id, { include = 'tasks,assignments,comments,attachments,media_assets,share_requests', skipCache = false, deleted = false } = {}) {
    const response = await api.get(`/v2/newsdesk/stories/${id}`, {
      params: { include, ...(deleted ? { deleted: 1 } : {}) },
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
    if (!response.data) {
      throw new Error(`Story with ID ${id} not found`)
    }
    return response.data
  },

  async getStoryWithDetails(id, skipCache = false, deleted = false, station) {
    const params = storiesListParams({
      id,
      stations: station ? [station] : undefined,
      trashed: deleted ? 'with' : undefined,
      include: 'tasks,assignments,comments,attachments,media_assets,share_requests,distributions'
    })
    const { data } = await v3.stories.list({ ...params, per_page: 1 }, cachePolicy(15 * 1000, skipCache))
    const story = data?.[0]
    if (!story) {
      throw new Error(`Story with ID ${id} not found`)
    }
    return story
  },

  async getCollection(master_id, skipCache = false, station) {
    let params = {
      include: 'tasks,assignments,comments,attachments,media_assets,share_requests'
    }
    const response = await api.get(`/v2/newsdesk/stories/${master_id}/collection`, {
      params: { ...params, 'filter[station_id][]': resolveStationId(station) },
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
    return response.data
  },
  /**
   * Every story matching the options, as an array (ND-2357). Pages at 100 under
   * the hood. Options are the same as fetchStories; the v2 scope words map to
   * dateWindow: today → 'today', active → 'today_and_tomorrow',
   * current → 'from_today' + includeUndated.
   */
  async getStories(options = {}, skipCache, include = 'assignments,comments') {
    const { rows } = await this.fetchAllStories({ include, ...options }, { skipCache: skipCache ?? false, maxAge: 5 * 1000 })
    return rows
  },

  async getMyStories(skipCache) {
    const today = dayjs().tz(vuex.getters.currentTimezone).format('YYYY-MM-DD')
    const { rows } = await this.fetchAllStories(
      { assignedTo: 'me', startDate: today, include: 'assignments,comments' },
      { skipCache: skipCache ?? false, maxAge: 5 * 1000 }
    )
    return rows
  },

  async getMyPitches(skipCache) {
    const today = dayjs().tz(vuex.getters.currentTimezone).format('YYYY-MM-DD')
    const { rows } = await this.fetchAllStories(
      { pitchedBy: 'me', startDate: today, include: 'assignments,comments' },
      { skipCache: skipCache ?? false, maxAge: 5 * 1000 }
    )
    return rows
  },

  async getPlannerHeaderStories(skipCache) {
    const { rows } = await this.fetchAllStories(
      { promotedNow: true, include: 'assignments,tasks,media_assets' },
      { skipCache: skipCache ?? false, maxAge: 30 * 1000 }
    )
    return rows
  },

  /** Every tag on the stories in the date range, as [{ name, ids }] (GET /v3/newsdesk/stories/tags). */
  async getStoriesTags(data, skipCache) {
    const { filter } = storiesListParams({ startDate: data?.tags_start_date, endDate: data?.tags_end_date })
    const { rows } = await v3.stories.allTags({ filter }, { ...cachePolicy(30 * 1000, skipCache), perPage: V3_PAGE_SIZE })
    return rows
  },

  // Queues the report and answers 202 with a job id, or 409 when the same station and
  // story selection is already running. No client timeout any more: the request returns
  // in milliseconds and the work is watched through getFccReportJob().
  generateFCCReport(station, storyIds) {
    const payload = {
      station,
      storyIds
    }
    return api.post(`/v2/newsdesk/generate-fcc-report`, payload)
  },

  // Polled while a run is in flight. `result` is only present once it has succeeded.
  getFccReportJob(jobId) {
    return api.get(`/v2/newsdesk/fcc-report-jobs/${jobId}`, {
      cache: {
        ignoreCache: true
      }
    })
  },

  getChanges() {
    const stationId = vuex.getters.currentStation.id
    if (!stationId) return
    // socket marks fallback polling (socket down) apart from the backstop
    // (socket up) in RUM and APM; the API ignores it (ND-2380).
    return api.get(`/v2/newsdesk/changes`, {
      params: { 'filter[station_id][]': stationId, socket: getSocketId() ? 'connected' : 'disconnected' },
      cache: {
        ignoreCache: true
      }
    })
  },
  updateStory(id, data) {
    return api.patch(`/v2/newsdesk/stories/${id}`, data)
  },
  updateStoryScript(id, scriptData) {
    return api.patch(`/v2/newsdesk/stories/${id}`, { script: scriptData })
  },
  deleteStory(row) {
    return api.delete(`/v2/newsdesk/stories/${row}`)
  },

  copyAssigneeFromStory(assignmentId) {
    return catchErrors.post(`/v2/newsdesk/assignments/${assignmentId}/copy-assignee-from-story`, {})
  },

  addAssignees(type, data) {
    const idKey = { stories: 'story_id', assignments: 'assignment_id', tasks: 'task_id' }[type]
    if (!idKey) throw new Error(`Unknown assignee type: ${type}`)
    const resourceId = data[idKey]
    if (!resourceId) throw new Error(`addAssignees(${type}) requires ${idKey}`)
    return catchErrors.post(`/v2/newsdesk/${type}/${resourceId}/assignees`, data)
  },

  updateAssignee(type, { id, roleId, userId }) {
    let data = {
      role_id: roleId,
      user_id: userId
    }
    return catchErrors.patch(`/v2/newsdesk/${type}/assignees/${id}`, data)
  },

  deleteAssignee(type, id) {
    return catchErrors.delete(`/v2/newsdesk/${type}/assignees/${id}`)
  },

  getAssignment(id) {
    return api.get(`/v2/newsdesk/assignments/${id}`)
  },

  async addAssignment(data) {
    if (!data?.story_id) throw new Error('Cannot add an assignment without a story id')
    if (!data?.platform) throw new Error('Cannot add an assignment without a platform')
    return catchErrors.post(`/v2/newsdesk/assignments`, data)
  },

  async updateAssignment(id, data) {
    return catchErrors.patch(`/v2/newsdesk/assignments/${id}`, data)
  },

  // Unused
  // updateAssignments(rows) {
  //   return api.patch(`/v2/newsdesk/assignments/bulk`, rows)
  // },

  deleteAssignment(row) {
    return api.delete(`/v2/newsdesk/assignments/${row}`)
  },
  deleteAssignments(rows) {
    return api.put(`/v2/newsdesk/assignments/bulk`, rows)
  },
  pushAssignment(assignmentId) {
    return api.post(`/v2/newsdesk/assignments/${assignmentId}/push`)
  },
  repushAssignment(assignmentId) {
    return api.post(`/v2/newsdesk/assignments/${assignmentId}/repush`)
  },

  // Script Methods
  getScripts(storyId, skipCache = false) {
    return api.get(`/v2/newsdesk/scripts/story/${storyId}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
  },
  getScript(scriptId, skipCache = false) {
    return api.get(`/v2/newsdesk/scripts/${scriptId}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
  },

  getScriptYjsState(scriptId) {
    return api.get(`/v2/newsdesk/scripts/${scriptId}/yjs-state`, {
      cache: { ignoreCache: true },
      headers: { 'X-Editor-Schema-Version': String(EDITOR_SCHEMA_VERSION) }
    })
  },
  saveScriptYjsState(scriptId, data) {
    return api.post(`/v2/newsdesk/scripts/${scriptId}/yjs-state`, data, {
      headers: { 'X-Editor-Schema-Version': String(EDITOR_SCHEMA_VERSION) }
    })
  },
  createScript(data) {
    return api.post(`/v2/newsdesk/scripts`, data)
  },
  updateScript(scriptId, data) {
    return api.patch(`/v2/newsdesk/scripts/${scriptId}`, data)
  },
  deleteScript(scriptId) {
    return api.delete(`/v2/newsdesk/scripts/${scriptId}`)
  },

  // Append script content to assignment rundown (one-way copy)
  appendScriptToAssignment(scriptId, assignmentId) {
    return api.post(`/v2/newsdesk/scripts/${scriptId}/append-to-assignment/${assignmentId}`)
  },

  // Clone a script (creates unlinked copy)
  cloneScript(scriptId) {
    return api.post(`/v2/newsdesk/scripts/${scriptId}/clone`)
  },

  // Set a script as primary for its story
  setScriptPrimary(scriptId) {
    return api.patch(`/v2/newsdesk/scripts/${scriptId}/set-primary`)
  },

  // Update script approval status
  updateScriptApproval(scriptId, approvalStatus) {
    return api.patch(`/v2/newsdesk/scripts/${scriptId}/approval`, {
      approval_status: approvalStatus
    })
  },

  // Script version history
  getScriptVersions(scriptId, limit = 50) {
    return api.get(`/v2/newsdesk/scripts/${scriptId}/versions`, { params: { limit } })
  },
  getScriptVersion(scriptId, versionId) {
    return api.get(`/v2/newsdesk/scripts/${scriptId}/versions/${versionId}`, { params: { include_state: true } })
  },
  createScriptVersion(scriptId) {
    return api.post(`/v2/newsdesk/scripts/${scriptId}/versions`)
  },
  restoreScriptVersion(scriptId, versionId) {
    return api.post(`/v2/newsdesk/scripts/${scriptId}/versions/${versionId}/restore`)
  },

  // Script change history
  getScriptChanges(scriptId, limit = 100) {
    return api.get(`/v2/newsdesk/scripts/${scriptId}/changes`, { params: { limit } })
  },

  /** The folo chain of a master story: the v3 envelope, `data` holding every linked story. */
  loadLinkedStories(masterId, includeDetails = false) {
    const params = storiesListParams({ masterId, include: includeDetails ? 'tasks,assignments,comments,attachments' : 'assignments' })
    return v3.stories.list({ ...params, per_page: STORIES_PER_PAGE_MAX })
  },
  /**
   * One page of stories by keyword and story date: the v3 envelope ({ data, meta }).
   * Dates may be Y-m-d or anything dayjs parses; they land in the workspace
   * timezone. `limit` is capped at the API's 400; use fetchAllStories for every row.
   */
  searchStories(keywords, startDate, endDate, limit = STORIES_PER_PAGE_MAX, skipCache) {
    const params = storiesListParams({ keywords, startDate, endDate, include: 'assignments,tasks,media_assets' })
    return v3.stories.list({ ...params, per_page: Math.min(limit, STORIES_PER_PAGE_MAX) }, cachePolicy(30 * 1000, skipCache))
  },
  /**
   * One page of stories (GET /v3/newsdesk/stories): the v3 envelope
   * { data, meta: { total, last_page, ... }, links }. See storiesListParams for
   * the options. `limit` becomes per_page and is capped at the API's 400.
   */
  fetchStories({ limit = V3_PAGE_SIZE, page, ...options } = {}, skipCache = true) {
    const params = storiesListParams({ include: DEFAULT_STORY_INCLUDE, ...options })
    return v3.stories.list({ ...params, per_page: Math.min(limit, STORIES_PER_PAGE_MAX), page }, cachePolicy(30 * 1000, skipCache))
  },

  /**
   * Every page of fetchStories, 100 rows per request, in order. Resolves with
   * { rows, total, truncated }; onPage(rows, { page, lastPage, total }) fires as
   * each page lands so a caller can draw progressively.
   */
  fetchAllStories(options = {}, { skipCache = true, maxAge = 30 * 1000, onPage, maxPages } = {}) {
    const { limit, page, ...rest } = options
    const params = storiesListParams({ include: DEFAULT_STORY_INCLUDE, ...rest })
    return v3.stories.listAll(params, { ...cachePolicy(maxAge, skipCache), perPage: V3_PAGE_SIZE, onPage, maxPages })
  },

  /**
   * One page of group rows (GET /v3/newsdesk/stories/groups): { data: [{ group_by,
   * value, count }], meta }. Same options as fetchStories plus groupBy; `sort`
   * is value | -value | count | -count.
   */
  fetchStoryGroups({ groupBy, sort, limit = V3_PAGE_SIZE, page, ...options }, skipCache = true) {
    const { filter } = storiesListParams(options)
    return v3.stories.groups({ group_by: groupBy, filter, sort, per_page: Math.min(limit, STORIES_PER_PAGE_MAX), page }, cachePolicy(30 * 1000, skipCache))
  },

  search(station, keywords) {
    return api.get(`/v2/newsdesk/search`, { params: { 'filter[station_id][]': resolveStationId(station), q: keywords } })
  },
  searchTasks(
    { keywords, stations, start_date, end_date, limit = 50, status = 'active', page = 1, pagination = false, order_by_field = null, sort = null },
    skipCache
  ) {
    stations = stations ?? [vuex.getters.currentStation.station]
    let params = {
      limit,
      page,
      sort,
      include: 'story',
      filter: {
        status: status,
        stations: stations?.join(',')
      },
      with_pagination: pagination
    }

    if (keywords) params.filter.keywords = keywords

    if (start_date && start_date.length > 4) {
      params['start_date>'] = start_date
    }

    if (end_date && end_date.length > 4) {
      params['end_date<'] = end_date
    }

    if (order_by_field) {
      params['order_by'] = order_by_field
    }

    return catchErrors.get(`/v2/newsdesk/tasks`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },

  searchScripts({ stations, approval_status, script_type, search, date_from, date_to, sort, page = 1, limit = 50 } = {}, skipCache = true) {
    stations = stations ?? [vuex.getters.currentStation.station]
    const params = {
      stations: [].concat(stations).join(','),
      page,
      limit
    }

    if (approval_status?.length) params.approval_status = [].concat(approval_status).join(',')
    if (script_type?.length) params.script_type = [].concat(script_type).join(',')
    if (search) params.search = search
    if (date_from) params.date_from = date_from
    if (date_to) params.date_to = date_to
    if (sort) params.sort = sort

    return catchErrors.get(`/v2/newsdesk/scripts`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache
      }
    })
  },

  getPendingScriptsCount({ stations, script_type, date_from, date_to } = {}) {
    stations = stations ?? [vuex.getters.currentStation.station]
    const params = { stations: [].concat(stations).join(',') }
    if (script_type?.length) params.script_type = [].concat(script_type).join(',')
    if (date_from) params.date_from = date_from
    if (date_to) params.date_to = date_to

    return catchErrors.get(`/v2/newsdesk/scripts/pending-count`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },

  async saveDraft(storyId, assignmentId, payload) {
    return api.post(`/v2/newsdesk/scripts/story/${storyId}/assignment/${assignmentId}`, payload)
  },

  async updateDraft(storyId, assignmentId, payload) {
    return api.patch(`/v2/newsdesk/scripts/story/${storyId}/assignment/${assignmentId}`, payload)
  },

  async deleteDraft(storyId, assignmentId) {
    return api.delete(`/v2/newsdesk/scripts/story/${storyId}/assignment/${assignmentId}`)
  },

  recoverStory(data, station) {
    return api.post(`/v2/newsdesk/stories/recover`, data, { params: { 'filter[station_id][]': resolveStationId(station) } })
  },

  shareStory(id, destinations) {
    let source = vuex.getters.currentStation.station
    let data = {
      story_id: id, // Required
      copyComments: true,
      copyAttachments: true,
      copyAssignmentsAsComments: true,
      copyTasks: true,
      copyMediaAssets: true
    }

    // convert to formdatavar form_data = new FormData();
    let formData = new FormData()
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key])
    })

    for (const d of destinations) {
      formData.append('stations[]', d)
    }

    return api.post(`/v2/newsdesk/stories/${source}/share`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },

  getShareRequests(params, skipCache) {
    const stationId = vuex.getters.currentStation.id
    return catchErrors.get(`/v2/newsdesk/share_requests`, {
      params: { ...params, 'filter[station_id][]': stationId },
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },

  async getPendingRequests(params, skipCache) {
    const stationId = vuex.getters.currentStation.id
    let resp = await catchErrors.get(`/v2/newsdesk/share_requests/${stationId}/inbound`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
    return resp?.data
  },

  getMonitoredStories(params, skipCache) {
    const stationId = vuex.getters.currentStation.id
    return catchErrors.get(`/v2/newsdesk/monitored_stories/${stationId}`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },

  addShareRequest({ storyId, mode, destinations, mediaAssets = [], station, expiresAt }) {
    if (!storyId || !Number.isInteger(Number(storyId))) {
      throw new Error(`addShareRequest called with invalid storyId: ${storyId}`)
    }

    // Callers pass the source story's station, which may be a name. The API
    // drops a non-numeric filter[station_id][] and then cannot find the story.
    const stationId = resolveStationId(station)

    let data = {
      story_id: storyId,
      mode,
      copyComments: true,
      copyAttachments: true,
      copyAssignmentsAsComments: true,
      copyTasks: true,
      copyMediaAssets: true
    }
    if (expiresAt) {
      data.expires_at = expiresAt
    }

    // convert to formdatavar form_data = new FormData();
    let formData = new FormData()
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key])
    })

    for (const d of destinations) {
      formData.append('stations[]', d)
    }
    for (const a of mediaAssets) {
      formData.append('mediaAssets[]', a)
    }

    return api.post(`/v2/newsdesk/share_requests`, formData, {
      params: { 'filter[station_id][]': stationId },
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },

  updateShareRequest(id, { response, mode, mediaAssets }) {
    return catchErrors.patch(`/v2/newsdesk/share_requests/${id}`, { response, mode, mediaAssets })
  },

  retractShareRequest(storyId) {
    const stationId = vuex.getters.currentStation.id
    return api.delete(`/v2/newsdesk/share_requests/retract/${storyId}`, { params: { 'filter[station_id][]': stationId } })
  },

  getUserSubscriptions(skipCache) {
    const station = vuex.getters.currentStation.station
    const params = {
      current_station: station,
      time: new Date().getTime()
    }
    return catchErrors.get('/v2/newsdesk/notifications/subscriptions', {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },
  checkStorySubscribedForNotification(storyId = '') {
    const station = vuex.getters.currentStation.station
    const params = {
      record_id: storyId || '',
      record_type: 'story',
      current_station: station,
      time: new Date().getTime()
    }
    return catchErrors.get(`/v2/newsdesk/notifications/check-subscription`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  storySubscribeForNotification(storyId = '') {
    const params = {
      record_id: storyId || '',
      record_type: 'story'
    }
    return catchErrors.post('/v2/newsdesk/notifications/subscribe', params, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  storyUnsubscribeFromNotification(storyId = '', subscribedNotificationType = '') {
    const params = {
      record_id: storyId || '',
      record_type: 'story',
      type: subscribedNotificationType || ''
    }
    return catchErrors.post('/v2/newsdesk/notifications/unsubscribe', params, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  getNotificationsSettings() {
    return catchErrors.get(`/v2/newsdesk/notifications/settings`)
  },

  updateNotificationsSettings(data) {
    return catchErrors.patch('/v2/newsdesk/notifications/settings', data)
  },
  resetNotificationsSettings(station) {
    return catchErrors.delete(`/v2/newsdesk/notifications/settings/${station}`)
  },

  saveOneSignalNotificationSubscription(data) {
    return catchErrors.post('/v2/newsdesk/notifications/onesignal-subscription', data)
  },

  storyNotifyCheck(storyId = '') {
    const params = {
      record_id: storyId || '',
      record_type: 'story',
      time: new Date().getTime()
    }
    return catchErrors.get(`/v2/newsdesk/notifications/notify/check`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  storyNotifyEnable(storyId = '') {
    const params = {
      record_id: storyId || '',
      record_type: 'story'
    }
    return catchErrors.post('/v2/newsdesk/notifications/notify/enable', params, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  storyNotifyDisable(storyId = '') {
    const params = {
      record_id: storyId || '',
      record_type: 'story'
    }
    return catchErrors.post('/v2/newsdesk/notifications/notify/disable', params, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  getApiNotifications(params = {}) {
    return catchErrors.get(`/v2/newsdesk/notifications/in_app/records`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  readApiNotification(notificationId = 0) {
    return catchErrors.post(
      `/v2/newsdesk/notifications/in_app/records/${notificationId}/read`,
      {},
      {
        cache: {
          maxAge: 0,
          exclude: {
            query: true
          }
        }
      }
    )
  },

  removeApiNotification(notificationId = 0) {
    return catchErrors.delete(`/v2/newsdesk/notifications/in_app/records/${notificationId}`, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  removeApiNotifications() {
    return catchErrors.delete('/v2/newsdesk/notifications/in_app/records', {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  checkApiNotifications(params = {}) {
    return catchErrors.get(`/v2/newsdesk/notifications/in_app/records/check`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  getApiNotificationEnabledStations(params = {}) {
    return catchErrors.get(`/v2/newsdesk/notifications/enabled_stations`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  setApiNotificationEnabledStations(data = {}) {
    return catchErrors.post(`/v2/newsdesk/notifications/enabled_stations`, data, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getNotificationGroupsList() {
    const stationId = vuex.getters.currentStation.id
    const params = {
      sort: 'title',
      order: 'asc',
      status: 1,
      // mention pickers also want the global groups that cover this station
      include_covering: 1,
      'filter[station_id][]': stationId
    }
    return api.get(`/v2/newsdesk/notifications/groups`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getNotificationGroups() {
    const stationId = vuex.getters.currentStation.id
    return api.get(`/v2/newsdesk/notifications/groups`, {
      params: { 'filter[station_id][]': stationId },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addNotificationGroup(data, station = null) {
    return api.post(`/v2/newsdesk/notifications/groups`, data, { params: { 'filter[station_id][]': resolveStationId(station) } })
  },
  updateNotificationGroup(id, data) {
    return api.patch(`/v2/newsdesk/notifications/groups/${id}`, data)
  },
  // Global notification groups: no station of their own, a covered-station list; global admins only.
  getGlobalNotificationGroups() {
    return api.get(`/v2/newsdesk/notifications/groups/global`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addGlobalNotificationGroup(data) {
    return api.post(`/v2/newsdesk/notifications/groups/global`, data)
  },
  updateGlobalNotificationGroup(id, data) {
    return api.patch(`/v2/newsdesk/notifications/groups/global/${id}`, data)
  },
  deleteGlobalNotificationGroup(id) {
    return api.delete(`/v2/newsdesk/notifications/groups/global/${id}`)
  },
  deleteNotificationGroup(id, station = null) {
    return api.delete(`/v2/newsdesk/notifications/groups/${id}`)
  },
  async deleteNotificationGroups(ids = []) {
    const stationId = vuex.getters.currentStation.id
    return catchErrors.delete(`/v2/newsdesk/notifications/groups`, {
      params: {
        ids,
        'filter[station_id][]': stationId
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getTokenizedAccesses() {
    const stationId = vuex.getters.currentStation.id
    return api.get(`/v2/newsdesk/tokenized-access`, {
      params: { 'filter[station_id][]': stationId },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addTokenizedAccess(data) {
    const stationId = vuex.getters.currentStation.id
    return api.post(`/v2/newsdesk/tokenized-access`, data, { params: { 'filter[station_id][]': stationId } })
  },
  updateTokenizedAccess(id, data) {
    return api.patch(`/v2/newsdesk/tokenized-access/${id}`, data)
  },
  deleteTokenizedAccess(id) {
    return api.delete(`/v2/newsdesk/tokenized-access/${id}`)
  },
  async deleteTokenizedAccesses(ids = []) {
    const stationId = vuex.getters.currentStation.id
    return catchErrors.delete(`/v2/newsdesk/tokenized-access`, {
      params: {
        ids,
        'filter[station_id][]': stationId
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getSelectedMediaAssetsDownloadsStats(params = {}) {
    return catchErrors.get(`/v2/newsdesk/media_assets/selected/downloads/stats`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getSelectedMediaAssetsDownloads(params = {}) {
    return catchErrors.get(`/v2/newsdesk/media_assets/selected/downloads`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  exportSelectedMediaAssetsDownloads(params = {}) {
    return catchErrors.get(`/v2/newsdesk/media_assets/selected/downloads/export`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      },
      headers: {
        'Content-Type': 'text/csv'
      }
    })
  },
  setSelectedMediaAssetsDownload(data) {
    return catchErrors.post(`/v2/newsdesk/media_assets/selected/download`, data)
  },
  updateSelectedMediaAssetsDownload(id, data) {
    return catchErrors.patch(`/v2/newsdesk/media_assets/selected/download/${id}`, data)
  },
  getBucketAndKeyFromUrls(mediaAssets = []) {
    const data = {
      mediaAssets
    }
    return catchErrors.post(`/v2/newsdesk/media_assets/selected/prepare_media_assets`, data)
  },
  prepareSelectedMediaAssetsDownloadCloudfrontLink(link) {
    const data = {
      link,
      mode: 'cloudfront_link'
    }
    return catchErrors.post(`/v2/newsdesk/media_assets/selected/prepare_media_assets`, data)
  },
  /**
   * One page of the Announcements grid (ND-1947): GET /v3/newsdesk/announcements.
   * params: { filter: { station_id, title, summary, priority, category, status }, sort, per_page, page }.
   * Returns { data, links, meta } (meta.total is the full count).
   */
  getAnnouncements(params = {}) {
    return api.get(`/v3/newsdesk/announcements`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getAnnouncement(id) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/announcements/${id}`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addAnnouncement(data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return catchErrors.post(`/v2/newsdesk/announcements`, data, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },
  updateAnnouncement(id, data) {
    data.station_name = data.station_name || vuex.getters.currentStation.station_name
    return api.patch(`/v2/newsdesk/announcements/${id}`, data)
  },
  deleteAnnouncement(id) {
    return api.delete(`/v2/newsdesk/announcements/${id}`)
  },
  async deleteAnnouncements(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/announcements`, {
      params: {
        station,
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  addAnnouncementAttachments(announcementId, data) {
    return api.post(`/v2/newsdesk/announcements/${announcementId}/attachments`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  getAnnouncementAttachments(announcementId, skipCache) {
    let station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/announcements/${announcementId}/attachments`, {
      params: {
        station
      },
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  deleteAnnouncementAttachment(announcementId, id) {
    return api.delete(`/v2/newsdesk/announcements/${announcementId}/attachments/${id}`)
  },
  getAnnouncementsListData(skipCache) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/announcements-list`, {
      params: {
        is_simple_list: true,
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getAnnouncementsArchiveListData(skipCache) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/announcements-list`, {
      params: {
        is_simple_list: true,
        station,
        mode: 'archive'
      },
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },
  addAnnouncementReadMark(data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/announcements-read-mark`, data)
  },
  recordAnnouncementView(announcementId) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/announcements-view`, { announcement_id: announcementId, station })
  },
  getAnnouncementSubscriptions() {
    return api.get(`/v2/newsdesk/announcement-subscriptions`, { params: { station: '' } })
  },
  updateAnnouncementSubscriptions(subscriptions) {
    return api.put(`/v2/newsdesk/announcement-subscriptions`, { station: '', subscriptions })
  },
  getStoryActivities(station, id, skipCache = false) {
    return api.get(`/v2/newsdesk/activity-log/story/${id}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
  },
  getMediaAssetActivities(station, id, skipCache = false) {
    return api.get(`/v2/newsdesk/activity-log/media-asset/${station}/${id}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
  },
  getInventoryCounts() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-counts`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getInventoryLocations() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-locations`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addInventoryLocation(data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/inventory-locations`, data)
  },
  updateInventoryLocation(id, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.patch(`/v2/newsdesk/inventory-locations/${id}`, data)
  },
  deleteInventoryLocation(id) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/inventory-locations/${id}`)
  },
  async deleteInventoryLocations(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/inventory-locations`, {
      params: {
        station,
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getInventoryTypes() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-types`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addInventoryType(data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/inventory-types`, data)
  },
  updateInventoryType(id, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.patch(`/v2/newsdesk/inventory-types/${id}`, data)
  },
  deleteInventoryType(id) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/inventory-types/${id}`)
  },
  async deleteInventoryTypes(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/inventory-types`, {
      params: {
        station,
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getInventoryTags() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-tags`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addInventoryTag(data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/inventory-tags`, data)
  },
  updateInventoryTag(id, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.patch(`/v2/newsdesk/inventory-tags/${id}`, data)
  },
  deleteInventoryTag(id) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/inventory-tags/${id}`)
  },
  async deleteInventoryTags(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/inventory-tags`, {
      params: {
        station,
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getInventoryAssets() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-assets`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addInventoryAsset(data) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/inventory-assets`, data, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },
  updateInventoryAsset(id, data) {
    const station = vuex.getters.currentStation.station
    data.append('_method', 'PATCH')
    return api.post(`/v2/newsdesk/inventory-assets/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },
  deleteInventoryAsset(id) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/inventory-assets/${id}`)
  },
  async deleteInventoryAssets(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/inventory-assets`, {
      params: {
        station,
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getInventoryAssetCheckouts(inventoryAssetId, skipCache) {
    let station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/checkouts`, {
      params: {
        station
      },
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  addInventoryAssetCheckout(inventoryAssetId, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/checkouts`, data)
  },
  updateInventoryAssetCheckout(inventoryAssetId, id, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.patch(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/checkouts/${id}`, data)
  },
  deleteInventoryAssetCheckout(inventoryAssetId, id) {
    return api.delete(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/checkouts/${id}`)
  },
  getInventoryAssetReservations(inventoryAssetId, skipCache) {
    let station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/reservations`, {
      params: {
        station
      },
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  addInventoryAssetReservation(inventoryAssetId, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/reservations`, data)
  },
  updateInventoryAssetReservation(inventoryAssetId, id, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.patch(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/reservations/${id}`, data)
  },
  deleteInventoryAssetReservation(inventoryAssetId, id) {
    return api.delete(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/reservations/${id}`)
  },
  getAssigneesInventoryAssetCheckoutsList(user_ids, skipCache) {
    const station = vuex.getters.currentStation.station
    let params = { station }

    // Build filter object for Spatie QueryBuilder
    if (user_ids && user_ids.length > 0) {
      params.filter = {
        user_id: user_ids
      }
    }

    return api.get(`/v2/newsdesk/inventory-assets/assignees-checkouts`, {
      params,
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getMyInventoryCheckoutsList(skipCache) {
    const station = vuex.getters.currentStation.station

    let params = {
      station: station,
      assigned: 'true'
    }

    return api.get(`/v2/newsdesk/inventory-assets/assignees-checkouts`, {
      params,
      cache: {
        maxAge: 5 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getAiProviders(params = {}) {
    return api.get(`/v2/newsdesk/ai-providers`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addAiProvider(data) {
    return api.post(`/v2/newsdesk/ai-providers`, data)
  },
  updateAiProvider(id, data) {
    return api.patch(`/v2/newsdesk/ai-providers/${id}`, data)
  },
  deleteAiProvider(id) {
    return api.delete(`/v2/newsdesk/ai-providers/${id}`)
  },
  async deleteAiProviders(ids = []) {
    return catchErrors.delete(`/v2/newsdesk/ai-providers`, {
      params: {
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getAiProviderOptions() {
    return api.get(`/v2/newsdesk/ai-providers/options`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getAiProviderAgents(aiProviderId, params = {}) {
    return api.get(`/v2/newsdesk/ai-providers/${aiProviderId}/agents`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addAiProviderAgent(aiProviderId, data) {
    return api.post(`/v2/newsdesk/ai-providers/${aiProviderId}/agents`, data)
  },
  discoverAiProviderAgents(aiProviderId) {
    return api.get(`/v2/newsdesk/ai-providers/${aiProviderId}/agents/discover`)
  },
  updateAiProviderAgent(aiProviderId, id, data) {
    return api.patch(`/v2/newsdesk/ai-providers/${aiProviderId}/agents/${id}`, data)
  },
  deleteAiProviderAgent(aiProviderId, id) {
    return api.delete(`/v2/newsdesk/ai-providers/${aiProviderId}/agents/${id}`)
  },
  async deleteAiProviderAgents(aiProviderId, ids = []) {
    return catchErrors.delete(`/v2/newsdesk/ai-providers/${aiProviderId}/agents`, {
      params: {
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getAiProviderAgentOptions() {
    return api.get(`/v2/newsdesk/ai-providers/options`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getAiSlotConfigs(params = {}) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/stations/${station}/ai-slot-configs`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addAiSlotConfig(data) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/stations/${station}/ai-slot-configs`, data)
  },
  updateAiSlotConfig(id, data) {
    return api.patch(`/v2/newsdesk/ai-slot-configs/${id}`, data)
  },
  deleteAiSlotConfig(id) {
    return api.delete(`/v2/newsdesk/ai-slot-configs/${id}`)
  },
  async deleteAiSlotConfigs(ids = []) {
    return catchErrors.delete(`/v2/newsdesk/ai-slot-configs`, {
      params: {
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getAiSlotConfigOptions() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/stations/${station}/ai-slot-configs/options`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getStoryDistributionsPlatforms(params = {}) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/platforms/${station}`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getStoryDistributions(id, params = {}) {
    return api.get(`/v2/newsdesk/stories/${id}/distributions`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  // The Uplynk clipping profiles a web platform's videos can publish with, read
  // live from the platform's workspace (ND-2532). A failure is a 502, never [].
  getPlatformClippingProfiles(platformId) {
    return api.get(`/v2/newsdesk/platforms/${platformId}/clipping-profiles`, {
      cache: { maxAge: 0, ignoreCache: true }
    })
  },
  getStoryDistributionsReadiness(id, params = {}) {
    return api.get(`/v2/newsdesk/stories/${id}/readiness`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addStoryDistribution(id, data) {
    return api.post(`/v2/newsdesk/stories/${id}/distributions`, data)
  },
  addStoryDistributionsBatch(id, data) {
    return api.post(`/v2/newsdesk/stories/${id}/distributions/batch`, data)
  },
  updateStoryDistribution(id, data) {
    return api.patch(`/v2/newsdesk/story-distributions/${id}`, data)
  },
  deleteStoryDistribution(id) {
    return api.delete(`/v2/newsdesk/story-distributions/${id}`)
  },
  publishStoryDistribution(id) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/publish`)
  },
  unpublishStoryDistribution(id) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/unpublish`)
  },
  sendDraftStoryDistribution(id) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/send-draft`)
  },
  discardDraftStoryDistribution(id) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/discard-draft`)
  },
  rejectStoryDistribution(id, note) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/reject`, {
      note
    })
  },
  cancelScheduleStoryDistribution(id) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/cancel-schedule`)
  },
  refreshStoryDistributionBody(id) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/refresh-body`)
  },
  // Read-only, but POST so the editor's unsaved fields can ride in the body.
  previewStoryDistribution(id, { status, story_script_id, metadata }) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/preview`, { status, story_script_id, metadata })
  },
  refreshStoryDistributionsForScript(scriptId) {
    return api.post(`/v2/newsdesk/story-scripts/${scriptId}/refresh-distributions`)
  },
  republishStoryDistributionsForScript(scriptId) {
    return api.post(`/v2/newsdesk/story-scripts/${scriptId}/republish-distributions`)
  },
  // Cut lists and summaries are refreshed from websocket events; never serve them from the axios cache
  getVideoCuts(mediaAssetId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts`, { cache: { ignoreCache: true } })
  },
  syncVideoCut(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/sync`)
  },
  ingestVideoCut(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/ingest`)
  },
  generateVideoCutReview(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/generate_review_link`)
  },
  getVideoCutStatus(mediaAssetId, videoCutId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/status`)
  },
  deleteVideoCut(mediaAssetId, videoCutId) {
    return api.delete(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/delete`)
  },
  restartVideoCutProcessing(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/restart`)
  },
  cancelVideoCut(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/cancel`)
  },
  getVideoCutWorkflows() {
    return api.get('/v2/newsdesk/video_cuts/workflows')
  },
  // Story-level video cuts (ND-2310): one home story, one or more source assets
  createVideoCut(payload) {
    return api.post('/v2/newsdesk/video_cuts', payload)
  },
  getStoryVideoCuts(storyId) {
    return api.get(`/v2/newsdesk/stories/${storyId}/video_cuts`, { cache: { ignoreCache: true } })
  },
  getStoryVideoCutsSummary(storyId) {
    return api.get(`/v2/newsdesk/stories/${storyId}/video_cuts/summary`, { cache: { ignoreCache: true } })
  },
  getWorkspaceVideoCuts(params = {}) {
    return api.get('/v2/newsdesk/video_cuts', { params, cache: { ignoreCache: true } })
  },
  getWorkspaceVideoCutsSummary(station) {
    return api.get('/v2/newsdesk/video_cuts/summary', { params: { station }, cache: { ignoreCache: true } })
  },
  getVideoCutChatMessages(mediaAssetId, videoCutId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/chat/messages`)
  },
  sendVideoCutChatMessage(mediaAssetId, videoCutId, payload) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/chat/messages`, payload)
  },
  getVideoCutChatStatus(mediaAssetId, videoCutId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/chat/status`)
  },
  interruptVideoCutChat(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/chat/interrupt`)
  },
  renderVideoCut(mediaAssetId, videoCutId, payload) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/renders`, payload)
  },
  ingestVideoCutRender(mediaAssetId, videoCutId, renderId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/renders/${renderId}/ingest`)
  },
  getVideoCutRenderPlaybackUrl(mediaAssetId, videoCutId, renderId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/renders/${renderId}/download`)
  },
  downloadVideoCutRenderFile(mediaAssetId, videoCutId, renderId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/renders/${renderId}/file`, { responseType: 'blob' })
  },

  getSequenceFormats(params = {}) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/stations/${station}/sequence-formats`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addSequenceFormat(data) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/stations/${station}/sequence-formats`, data)
  },
  updateSequenceFormat(id, data) {
    return api.patch(`/v2/newsdesk/sequence-formats/${id}`, data)
  },
  deleteSequenceFormat(id) {
    return api.delete(`/v2/newsdesk/sequence-formats/${id}`)
  },
  async deleteSequenceFormats(ids = []) {
    return catchErrors.delete(`/v2/newsdesk/sequence-formats`, {
      params: {
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getSequenceFormatOptions() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/stations/${station}/sequence-formats/options`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  }
}
